import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, MessageCircle, HelpCircle, Bell, LogOut, User, Settings, CreditCard, Globe, ArrowUpRight } from 'lucide-react';
import SonicHeaderPill, { SonicHeaderIconPill } from './ui/SonicHeaderPill';
import { authService } from '../services/authService';
import { useAuth } from '../hooks/useAuth';
import { settingsService } from '../services/settingsService';
import NotificationDropdown from './NotificationDropdown';
import { useToast } from './Toast';
import { chatService } from '../services/chatService';
import { supportChatService } from '../services/supportChatService';
import { socketService } from '../services/socketService';
import { getMediaUrl } from '../utils/media';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, loading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isAuthenticated = !!user;
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [checkViewEnabled, setCheckViewEnabled] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection for nav link glow effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'vi' ? 'en' : 'vi';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    authService.logout();
    setIsProfileMenuOpen(false);
    window.location.href = '/';
  };

  const submitGlobalSearch = () => {
    const query = globalSearchTerm.trim();
    if (!query) {
      showToast(t('common.searchWarning'), 'warning');
      return;
    }
    navigate(`/community?search=${encodeURIComponent(query)}`);
    setGlobalSearchTerm('');
    setIsMobileMenuOpen(false);
  };

  const handleGlobalSearchSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }
    submitGlobalSearch();
  };

  const isActive = (path: string) => location.pathname === path;

  // Fetch unread messages count
  const fetchUnreadMessagesCount = React.useCallback(async () => {
    if (!user) return;
    try {
      const conversations = await chatService.getConversations();
      const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (error) {
      // Silent fail
    }
  }, [user]);

  const fetchSupportUnreadCount = React.useCallback(async () => {
    if (!user) return;
    try {
      const conversations = await supportChatService.getConversations();
      const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setSupportUnreadCount(totalUnread);
    } catch (error) {
      // Silent fail
    }
  }, [user]);

  // Socket and message listeners
  useEffect(() => {
    let isMounted = true;
    if (!user) {
      setUnreadMessagesCount(0);
      setSupportUnreadCount(0);
      return;
    }

    const initialFetchTimer = setTimeout(() => {
      if (isMounted) {
        fetchUnreadMessagesCount();
        fetchSupportUnreadCount();
      }
    }, 1000);

    const socketTimer = setTimeout(() => {
      if (isMounted && !socketService.isConnected()) {
        try {
          socketService.connect();
        } catch (e) {
          // Ignore
        }
      }
    }, 2000);

    const unsubscribeNewMessage = chatService.onNewMessage((message) => {
      if (!isMounted) return;
      if (message.sender_id !== user.id) {
        setTimeout(() => {
          if (isMounted) fetchUnreadMessagesCount();
        }, 2000);
      }
    });

    const unsubscribeConversationUpdate = chatService.onConversationUpdate(() => {
      if (!isMounted) return;
      setTimeout(() => {
        if (isMounted) fetchUnreadMessagesCount();
      }, 2000);
    });

    const unsubscribeSupportNewMessage = supportChatService.onNewMessage((message) => {
      if (!isMounted) return;
      if (message.sender_id !== user.id) {
        setTimeout(() => {
          if (isMounted) fetchSupportUnreadCount();
        }, 2000);
      }
    });

    const unsubscribeSupportConversationUpdate = supportChatService.onConversationUpdate(() => {
      if (!isMounted) return;
      setTimeout(() => {
        if (isMounted) fetchSupportUnreadCount();
      }, 2000);
    });

    const pollInterval = setInterval(() => {
      if (isMounted && !socketService.isConnected()) {
        fetchUnreadMessagesCount();
        fetchSupportUnreadCount();
      }
    }, 60000);

    return () => {
      isMounted = false;
      clearTimeout(initialFetchTimer);
      clearTimeout(socketTimer);
      unsubscribeNewMessage();
      unsubscribeConversationUpdate();
      unsubscribeSupportNewMessage();
      unsubscribeSupportConversationUpdate();
      clearInterval(pollInterval);
    };
  }, [user, fetchUnreadMessagesCount, fetchSupportUnreadCount]);

  useEffect(() => {
    if (user) {
      fetchUnreadMessagesCount();
      fetchSupportUnreadCount();
    }
  }, [location.pathname, user, fetchUnreadMessagesCount, fetchSupportUnreadCount]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  // Load check view setting
  useEffect(() => {
    const loadSetting = async () => {
      try {
        const enabled = await settingsService.isCheckViewEnabled();
        setCheckViewEnabled(enabled);
      } catch (error) {
        setCheckViewEnabled(true);
      }
    };
    loadSetting();
  }, []);

  const baseNavLinks = [
    { path: '/', label: t('menu.home') },
    { path: '/tools', label: t('menu.tools') },
    { path: '/community', label: t('menu.community') },
    { path: '/event', label: t('menu.events') },
    { path: '/classes', label: t('menu.classes'), adminOnly: true },
    { path: '/tasks', label: t('menu.tasks') },
    { path: '/transcript', label: t('menu.transcript') },
    { path: '/check-view', label: t('menu.checkView'), checkViewRequired: true },
    { path: '/free-keys', label: t('menu.freeKeys') },
    { path: '/calendar', label: t('menu.calendar'), adminOnly: true },
    { path: '/members', label: t('menu.members') },
    { path: '/leaderboard', label: t('menu.leaderboard') },
    { path: '/about', label: t('menu.about') }
  ];

  const navLinks = baseNavLinks.filter(link => {
    if (link.adminOnly && user?.role !== 'admin') return false;
    if (link.checkViewRequired && !checkViewEnabled) return false;
    return true;
  });

  // Admin Navbar
  if (isAdminRoute && !loading && user?.role === 'admin') {
    return (
      <nav className="bg-[var(--background-dark)] border-b border-white/10 sticky top-0 z-50 transition-colors text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/admin" className="flex items-center gap-2 font-semibold text-base text-white hover:text-white/90">
              <span>🛠️</span>
              <span className="hidden sm:inline">{t('menu.adminArea')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <span className="hidden md:block text-sm text-muted-foreground">
                {user.full_name || user.username}
              </span>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white border-none" size="sm" asChild>
                <Link to="/">{t('menu.studentPage')}</Link>
              </Button>
              <Button variant="destructive" size="sm" onClick={handleLogout}>
                {t('menu.logout')}
              </Button>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Main Navbar
  return (
    <nav className="bg-[var(--background-dark)] border-b border-white/10 sticky top-0 z-50 shadow-sm transition-colors text-white">
      <div className="w-full px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center flex-shrink-0 gap-2">
            <img src="/images/logo.png" alt="Logo" className="w-12 h-12 object-contain" />
            <span className="text-base sm:text-lg font-extrabold text-white">TaoThaoAIPro</span>
          </Link>

          {/* Desktop Navigation - Centered with spacing */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-1 px-8">
            {navLinks.slice(0, 6).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="group relative inline-block hover:-translate-y-0.5 transition-transform duration-200"
              >
                {/* Outer blur glow */}
                <div className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full blur-sm group-hover:blur group-hover:brightness-125 transition opacity-75"></div>

                {/* Main content wrapper */}
                <div className={cn(
                  "relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic",
                  isActive(link.path) && "brightness-110"
                )}>
                  {/* Inner pill body */}
                  <div className={cn(
                    "flex items-center justify-center bg-black/85 rounded-full transition px-3 py-1.5 text-xs font-semibold whitespace-nowrap group-hover:bg-black/75 backdrop-blur-sm",
                    isActive(link.path)
                      ? "bg-black/90"
                      : ""
                  )}>
                    <span className="text-white/85 font-semibold transition group-hover:text-white">
                      {link.label}
                    </span>
                  </div>

                  {/* Shimmer overlay */}
                  <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep"></div>
                  </div>
                </div>
              </Link>
            ))}

            {/* More menu for remaining links */}
            {navLinks.length > 6 && (
              <div className="relative group">
                <div className="relative inline-block hover:-translate-y-0.5 transition-transform duration-200">
                  {/* Outer blur glow */}
                  <div className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full blur-sm group-hover:blur group-hover:brightness-125 transition opacity-75"></div>

                  {/* Main content wrapper */}
                  <div className="relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic">
                    {/* Inner pill body */}
                    <button className="flex items-center justify-center bg-black/85 rounded-full transition px-3 py-1.5 text-xs font-semibold group-hover:bg-black/75 backdrop-blur-sm">
                      <span className="text-white/85 font-semibold transition group-hover:text-white">
                        {t('menu.more')}
                      </span>
                    </button>

                    {/* Shimmer overlay */}
                    <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                      <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep"></div>
                    </div>
                  </div>
                </div>
                <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {navLinks.slice(6).map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2.5 text-sm font-medium text-slate-900 hover:bg-primary-soft hover:text-primary-soft-foreground transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {user?.role === 'admin' && (
              <Link
                to="/admin/users"
                className="group relative inline-block hover:-translate-y-0.5 transition-transform duration-200"
              >
                {/* Outer blur glow */}
                <div className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full blur-sm group-hover:blur group-hover:brightness-125 transition opacity-75"></div>

                {/* Main content wrapper */}
                <div className={cn(
                  "relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic",
                  location.pathname.startsWith('/admin') && "brightness-110"
                )}>
                  {/* Inner pill body */}
                  <div className={cn(
                    "flex items-center justify-center bg-black/85 rounded-full transition px-3 py-1.5 text-xs font-semibold group-hover:bg-black/75 backdrop-blur-sm",
                    location.pathname.startsWith('/admin')
                      ? "bg-black/90"
                      : ""
                  )}>
                    <span className="text-white/85 font-semibold transition group-hover:text-white">
                      {t('menu.admin')}
                    </span>
                  </div>

                  {/* Shimmer overlay */}
                  <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep"></div>
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle - with glow effect */}
            <div className="hidden md:block">
              <SonicHeaderPill
                onClick={toggleLanguage}
                icon={<Globe className="h-4 w-4" />}
                label={i18n.language === 'en' ? 'EN' : 'VI'}
              />
            </div>

            {/* Search - Desktop */}
            <form
              className="hidden md:flex items-center"
              onSubmit={handleGlobalSearchSubmit}
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('common.searchPlaceholder')}
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  className="w-40 xl:w-56 pl-9 pr-4 py-2 text-sm rounded-lg border border-transparent bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-slate-800 placeholder:text-slate-400"
                />
              </div>
            </form>

            {isAuthenticated && (
              <>
                {/* Chat - Desktop with glow */}
                <div className="hidden md:block">
                  <SonicHeaderIconPill
                    to="/chat"
                    icon={<MessageCircle className="h-5 w-5" />}
                    badge={unreadMessagesCount}
                    badgeColor="bg-destructive"
                  />
                </div>

                {/* Support - Desktop with glow */}
                <div className="hidden md:block">
                  <SonicHeaderIconPill
                    to="/support"
                    icon={<HelpCircle className="h-5 w-5" />}
                    badge={supportUnreadCount}
                    badgeColor="bg-info"
                  />
                </div>

                {/* Notifications - Desktop */}
                <div className="hidden md:block">
                  <NotificationDropdown />
                </div>

                {/* Profile Menu */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="w-8 h-8 rounded-full overflow-hidden border-2 border-border hover:border-primary transition focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {user?.avatar ? (
                      <img
                        src={getMediaUrl(user.avatar)}
                        alt={user.full_name || user.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                        {user?.full_name?.[0]?.toUpperCase() || user?.username[0]?.toUpperCase() || 'T'}
                      </div>
                    )}
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-surface rounded-lg shadow-lg border border-border z-50 overflow-hidden">
                      <div className="p-3 border-b border-border bg-muted/30">
                        <p className="text-sm font-semibold text-foreground truncate">{user?.full_name || user?.username}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/profile"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition"
                        >
                          <User className="h-4 w-4 text-muted-foreground" />
                          {t('menu.profile')}
                        </Link>
                        {user?.role === 'admin' && (
                          <Link
                            to="/payment"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-success hover:bg-success-soft transition"
                          >
                            <CreditCard className="h-4 w-4" />
                            {t('menu.payment')}
                          </Link>
                        )}
                        <Link
                          to="/settings"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition"
                        >
                          <Settings className="h-4 w-4 text-muted-foreground" />
                          {t('menu.settings')}
                        </Link>
                        <div className="border-t border-border my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-destructive-soft transition text-left"
                        >
                          <LogOut className="h-4 w-4" />
                          {t('menu.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Not authenticated */}
            {!isAuthenticated && (
              <div className="hidden md:flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild className="h-8 text-xs">
                  <Link to="/login">{t('menu.login')}</Link>
                </Button>
                <Button size="sm" asChild className="h-8 text-xs">
                  <Link to="/register">{t('menu.register')}</Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-8 w-8 text-white hover:bg-white/10 hover:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-3 space-y-1 border-t border-border">
            {/* Mobile Search */}
            <div className="flex gap-2 px-2 mb-3">
              <form className="flex-1" onSubmit={handleGlobalSearchSubmit}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={t('common.searchPlaceholder')}
                    value={globalSearchTerm}
                    onChange={(e) => setGlobalSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </form>
              <Button variant="ghost" size="icon" onClick={toggleLanguage} className="shrink-0 border border-input h-9 w-9">
                <span className="text-xs font-bold">{i18n.language === 'en' ? 'EN' : 'VI'}</span>
              </Button>
            </div>

            {/* Mobile Icons Row */}
            {isAuthenticated && (
              <div className="flex items-center gap-2 px-2 pb-2 border-b border-border">
                <Button variant="ghost" size="icon" className="relative h-9 w-9" asChild>
                  <Link to="/chat" onClick={() => setIsMobileMenuOpen(false)}>
                    <MessageCircle className="h-5 w-5" />
                    {unreadMessagesCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-destructive text-destructive-foreground text-[9px] rounded-full flex items-center justify-center font-bold px-1">
                        {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                      </span>
                    )}
                  </Link>
                </Button>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 text-info" asChild>
                  <Link to="/support" onClick={() => setIsMobileMenuOpen(false)}>
                    <HelpCircle className="h-5 w-5" />
                    {supportUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-info text-info-foreground text-[9px] rounded-full flex items-center justify-center font-bold px-1">
                        {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
                      </span>
                    )}
                  </Link>
                </Button>
              </div>
            )}

            {/* Mobile Links */}
            <div className="space-y-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "block px-4 py-2.5 text-sm font-semibold rounded-md transition-colors",
                    isActive(link.path)
                      ? "bg-white/10 text-white"
                      : "text-white hover:bg-white/10 hover:text-white"
                  )}
                >
                  {link.label}
                </Link>
              ))}

              {user?.role === 'admin' && (
                <Link
                  to="/admin/users"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "block px-4 py-2.5 text-sm font-semibold rounded-md transition-colors",
                    location.pathname.startsWith('/admin')
                      ? "bg-white/10 text-white"
                      : "text-white hover:bg-white/10 hover:text-white"
                  )}
                >
                  {t('menu.admin')}
                </Link>
              )}
            </div>

            {/* Mobile Auth Actions */}
            {isAuthenticated ? (
              <div className="border-t border-border pt-2 mt-2 space-y-1 px-2">
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 hover:text-white rounded-md"
                >
                  {t('menu.profile')}
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 hover:text-white rounded-md"
                >
                  {t('menu.settings')}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive-soft rounded-md"
                >
                  {t('menu.logout')}
                </button>
              </div>
            ) : (
              <div className="border-t border-border pt-2 mt-2 space-y-2 px-2">
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('menu.login')}
                  </Link>
                </Button>
                <Button className="w-full" asChild>
                  <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                    {t('menu.register')}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
